import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { useToast } from "./Toast";
import { Button, Field, inputClass } from "./ui";

/**
 * One right-hand drawer for "Add vendor", "Add investor", "Add buyer" and their edits (v2.1 T6.1).
 *
 * The three records differ only in their fields, so the drawer takes a field list rather than being
 * written three times. Money is typed in dollars and sent in cents; lists are comma separated; every
 * save goes through the record's own endpoint, which writes the ledger row.
 */
export type DrawerField = {
  key: string;
  label: string;
  type?: "text" | "number" | "money" | "select" | "list" | "bool" | "textarea";
  options?: readonly string[];
  required?: boolean;
  hint?: string;
};

type Values = Record<string, unknown>;

function toInput(field: DrawerField, value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (field.type === "money") return String(Number(value) / 100);
  if (field.type === "list" && Array.isArray(value)) return value.join(", ");
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "";
}

function fromInput(field: DrawerField, raw: string, checked: boolean): unknown {
  if (field.type === "bool") return checked;
  const value = raw.trim();
  if (field.type === "list") {
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  if (value === "") return null;
  if (field.type === "money") return Math.round(Number(value.replace(/[$,\s]/g, "")) * 100);
  if (field.type === "number") return Number(value);
  return value;
}

export function RecordDrawer({
  title,
  fields,
  initial,
  submitLabel,
  onSubmit,
  invalidate,
  onClose,
  testId,
}: {
  title: string;
  fields: DrawerField[];
  initial?: Values;
  submitLabel: string;
  onSubmit: (values: Values) => Promise<unknown>;
  invalidate: string[];
  onClose: () => void;
  testId: string;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [raw, setRaw] = useState<Record<string, string>>(() =>
    Object.fromEntries(fields.map((f) => [f.key, toInput(f, initial?.[f.key] ?? (f.type === "select" ? f.options?.[0] : ""))])),
  );
  const [flags, setFlags] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(fields.filter((f) => f.type === "bool").map((f) => [f.key, Boolean(initial?.[f.key])])),
  );

  const save = useMutation({
    mutationFn: () =>
      onSubmit(
        Object.fromEntries(
          fields.map((f) => [f.key, fromInput(f, raw[f.key] ?? "", flags[f.key] ?? false)]),
        ),
      ),
    onSuccess: () => {
      toast.push(`${title} — saved`);
      invalidate.forEach((key) => void qc.invalidateQueries({ queryKey: [key] }));
      onClose();
    },
    onError: (e: Error) => toast.push(e.message, "danger"),
  });

  const missing = fields.some((f) => f.required && !(raw[f.key] ?? "").trim());

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" role="dialog" aria-modal="true" aria-label={title}>
      <aside className="flex h-full w-full max-w-md flex-col border-l bg-popover shadow-xl" data-testid={testId}>
        <header className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button type="button" aria-label="Close" className="rounded p-1 text-fg-muted hover:bg-surface-2" onClick={onClose}>
            <X className="size-4" aria-hidden />
          </button>
        </header>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
          {fields.map((field) => (
            <Field key={field.key} label={field.label} required={field.required} hint={field.hint}>
              {field.type === "select" ? (
                <select
                  value={raw[field.key] ?? ""}
                  className={inputClass}
                  data-testid={`${testId}-${field.key}`}
                  onChange={(e) => setRaw((r) => ({ ...r, [field.key]: e.target.value }))}
                >
                  {(field.options ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              ) : field.type === "bool" ? (
                <input
                  type="checkbox"
                  checked={flags[field.key] ?? false}
                  data-testid={`${testId}-${field.key}`}
                  onChange={(e) => setFlags((f) => ({ ...f, [field.key]: e.target.checked }))}
                />
              ) : field.type === "textarea" ? (
                <textarea
                  rows={3}
                  value={raw[field.key] ?? ""}
                  className={inputClass}
                  data-testid={`${testId}-${field.key}`}
                  onChange={(e) => setRaw((r) => ({ ...r, [field.key]: e.target.value }))}
                />
              ) : (
                <input
                  value={raw[field.key] ?? ""}
                  inputMode={field.type === "money" || field.type === "number" ? "decimal" : undefined}
                  className={inputClass}
                  data-testid={`${testId}-${field.key}`}
                  onChange={(e) => setRaw((r) => ({ ...r, [field.key]: e.target.value }))}
                />
              )}
            </Field>
          ))}
        </div>
        <footer className="flex justify-end gap-2 border-t px-4 py-3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={missing || save.isPending} data-testid={`${testId}-save`} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : submitLabel}
          </Button>
        </footer>
      </aside>
    </div>
  );
}
